import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { logoDark, logoLight } from '@react-apps/design-system';
import { useAuthStore } from '@/modules/auth/presentation/store/auth.store';
import { authContainer } from '@/modules/auth/di/container';
import { useProfile } from '@/modules/profile/presentation/hooks/use-profile';
import { ViewAsBanner } from '@/shared/view-as/ViewAsControl';
import { ImpersonationBanner } from '@/shared/impersonation/ImpersonationBanner';
import { LogoutIcon } from '@/shared/icons';

interface HeaderProps {
  isCollapsed: boolean;
  onToggleSidebar: () => void;
}

export function Header({ isCollapsed, onToggleSidebar }: HeaderProps) {
  const { auth, clearAuth } = useAuthStore();
  const { data: profile } = useProfile();
  const navigate = useNavigate();

  const displayName = profile?.name ?? auth?.email.split('@')[0] ?? 'Usuário';
  const initials = profile?.name
    ? profile.name
        .split(' ')
        .slice(0, 2)
        .map((w: string) => w[0])
        .join('')
        .toUpperCase()
    : (auth?.email?.[0]?.toUpperCase() ?? 'U');

  // Só observa o tema (controlado no Settings) para trocar o logo claro/escuro.
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fecha o dropdown ao clicar fora.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const toggleTheme = () => {
    const next = !document.documentElement.classList.contains('dark');
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  // "Minha Conta": pede um custom token ao gateway e redireciona ao
  // user-dashboard, que troca o token por uma sessão Firebase (SSO handoff).
  const goToMyAccount = async () => {
    setMenuOpen(false);
    const userDashboardUrl = import.meta.env.VITE_USER_DASHBOARD_URL as
      | string
      | undefined;
    if (!userDashboardUrl) {
      console.error('VITE_USER_DASHBOARD_URL não configurado');
      return;
    }
    try {
      const { customToken } =
        await authContainer.repository.createHandoffToken();
      window.location.href = `${userDashboardUrl}/handoff#ct=${encodeURIComponent(
        customToken,
      )}`;
    } catch (err) {
      console.error('Falha ao iniciar handoff para o user-dashboard', err);
    }
  };

  const handleLogout = () => {
    setMenuOpen(false);
    void authContainer.repository.clearAuth();
    clearAuth();
    void navigate('/sign-in');
  };

  return (
    <>
      <ImpersonationBanner />
      <ViewAsBanner />
      <header className="border-(--color-border) bg-(--color-bg-primary) flex h-16 items-center justify-between border-b px-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSidebar}
            className="text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) cursor-pointer rounded-lg p-2 transition-colors"
            aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5"
              />
            </svg>
          </button>

          <img
            src={isDark ? logoDark : logoLight}
            alt="Primum"
            className="h-4 w-auto"
          />
        </div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="hover:bg-(--color-bg-secondary) flex items-center gap-3 rounded-lg p-1 transition-colors"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Menu da conta"
          >
            <div className="hidden flex-col items-end sm:flex">
              <p className="text-(--color-text-primary) text-sm font-semibold leading-tight">
                {displayName}
              </p>
              {auth?.email && (
                <p className="text-(--color-text-secondary) text-xs leading-tight">
                  {auth.email}
                </p>
              )}
            </div>

            <div className="bg-(--color-accent) flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium text-white">
              {initials}
            </div>
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="border-(--color-border) bg-(--color-bg-primary) absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-lg border shadow-lg"
            >
              <button
                role="menuitem"
                onClick={goToMyAccount}
                className="text-(--color-text-primary) hover:bg-(--color-bg-secondary) flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors"
              >
                <UserCircleIcon className="h-5 w-5 shrink-0" />
                Minha Conta
              </button>

              <button
                role="menuitem"
                onClick={toggleTheme}
                className="text-(--color-text-primary) hover:bg-(--color-bg-secondary) flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors"
              >
                {isDark ? (
                  <SunIcon className="h-5 w-5 shrink-0" />
                ) : (
                  <MoonIcon className="h-5 w-5 shrink-0" />
                )}
                {isDark ? 'Tema claro' : 'Tema escuro'}
              </button>

              <div className="border-(--color-border) border-t" />

              <button
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-500 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50"
              >
                <LogoutIcon className="h-5 w-5 shrink-0" />
                Sair
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
}

function UserCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  );
}

function SunIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z"
      />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z"
      />
    </svg>
  );
}
